<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, ref } from 'vue';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const store = useStore();
const { t } = useI18n();
const getters = useStoreGetters();
const teamsList = computed(() => getters['teams/getTeams'].value);
const uiFlags = computed(() => getters['teams/getUIFlags'].value);

const loading = ref({});
const searchQuery = ref('');

// Filtered teams based on search
const filteredTeamsList = computed(() => {
  if (!searchQuery.value) return teamsList.value;
  const query = searchQuery.value.toLowerCase();
  return teamsList.value.filter(
    team =>
      team.name.toLowerCase().includes(query) ||
      (team.description && team.description.toLowerCase().includes(query))
  );
});

const deleteTeam = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('teams/delete', id);
    useAlert(t('TEAMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedTeam = ref({});

const openDelete = team => {
  showDeletePopup.value = true;
  selectedTeam.value = team;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedTeam.value = {};
};

const confirmDeletion = () => {
  deleteTeam(selectedTeam.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () => `${t('TEAMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedTeam.value.name}`
);

const deleteRejectText = computed(() => t('TEAMS_SETTINGS.DELETE.CONFIRM.NO'));

const confirmDeleteTitle = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    teamName: selectedTeam.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('TEAMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    teamName: selectedTeam.value.name,
  })
);
</script>

<template>
  <div class="teams-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="ws-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center gap-2">
        <span class="i-ph-users-three-bold size-5" style="color: #6b7280" />
        <h1 class="text-xl font-semibold" style="color: #111827">
          {{ $t('TEAMS_SETTINGS.HEADER') }}
        </h1>
      </div>
      <router-link :to="{ name: 'settings_teams_new' }">
        <button class="ws-btn">
          <span class="i-ph-plus-bold size-3.5" />
          {{ $t('TEAMS_SETTINGS.NEW_TEAM') }}
        </button>
      </router-link>
    </header>

    <!-- Search toolbar -->
    <div class="ws-toolbar flex items-center gap-3 px-6 py-3">
      <div class="relative flex-1 max-w-xs">
        <span
          class="absolute left-3 top-1/2 -translate-y-1/2 i-ph-magnifying-glass-bold size-4 pointer-events-none"
          style="color: #9ca3af"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="ws-search reset-base w-full pl-10 pr-4 py-2.5 text-sm rounded-lg"
          :placeholder="
            $t('TEAMS_SETTINGS.SEARCH.PLACEHOLDER') || 'Search teams...'
          "
        />
      </div>
      <span class="ws-count-pill">{{ filteredTeamsList.length }} teams</span>
    </div>

    <!-- Content -->
    <div class="ws-content flex-1 overflow-y-auto p-6">
      <div
        v-if="uiFlags.isFetching"
        class="flex items-center justify-center py-20"
      >
        <woot-loading-state :message="$t('TEAMS_SETTINGS.LOADING')" />
      </div>
      <div v-else-if="!filteredTeamsList.length" class="ws-empty">
        <div class="ws-empty-icon">
          <span class="i-ph-users-three-bold size-10" style="color: #9ca3af" />
        </div>
        <p style="color: #6b7280">{{ $t('TEAMS_SETTINGS.LIST.404') }}</p>
      </div>
      <div v-else class="ws-table-wrap">
        <table class="ws-table">
          <thead class="ws-thead">
            <tr>
              <th class="ws-th">Name</th>
              <th class="ws-th">Description</th>
              <th class="ws-th ws-th--right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="team in filteredTeamsList" :key="team.id" class="ws-tr">
              <td class="ws-td">
                <span class="font-semibold" style="color: #111827">{{
                  team.name
                }}</span>
              </td>
              <td class="ws-td" style="color: #4b5563">
                {{ team.description || '—' }}
              </td>
              <td class="ws-td ws-td--right">
                <div class="flex justify-end gap-2">
                  <router-link
                    :to="{
                      name: 'settings_teams_edit',
                      params: { teamId: team.id },
                    }"
                  >
                    <Button
                      v-tooltip.top="$t('TEAMS_SETTINGS.LIST.EDIT_TEAM')"
                      icon="i-ph-pencil-simple-bold"
                      slate
                      xs
                      faded
                    />
                  </router-link>
                  <Button
                    v-tooltip.top="$t('TEAMS_SETTINGS.DELETE.BUTTON_TEXT')"
                    icon="i-ph-trash-bold"
                    xs
                    ruby
                    faded
                    :is-loading="loading[team.id]"
                    @click="openDelete(team)"
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Delete Modal -->
    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('TEAMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedTeam.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>

<style scoped>
.teams-page,
.dark .teams-page {
  background: #ffffff !important;
}
.ws-header {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}
.ws-toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
}
.ws-content {
  background: #ffffff;
}
.ws-search {
  background: #f9fafb !important;
  border: 1px solid #e5e7eb !important;
  color: #111827 !important;
}
.ws-search:focus {
  outline: none;
  border-color: #0066ff !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  background: #fff !important;
}
.ws-search::placeholder {
  color: #9ca3af !important;
}
.ws-table-wrap {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
}
.ws-table {
  border-collapse: collapse;
  width: 100%;
}
.ws-thead {
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
}
.ws-th {
  padding: 10px 24px;
  text-align: left;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: #374151 !important;
  background: #f9fafb !important;
}
.ws-th--right {
  text-align: right;
}
.ws-tr {
  border-bottom: 1px solid #f3f4f6;
  transition: background 0.12s ease;
}
.ws-tr:last-child {
  border-bottom: none;
}
.ws-tr:hover {
  background: #fafafa;
}
.ws-td {
  padding: 14px 24px;
  font-size: 13px;
  color: #111827;
  vertical-align: middle;
}
.ws-td--right {
  text-align: right;
}
.ws-count-pill {
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
}
.ws-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 16px;
  font-size: 13px;
  font-weight: 600;
  font-family: 'Excon', system-ui, sans-serif;
  background: #111827;
  color: #fff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.ws-btn:hover {
  background: #1f2937;
}
.ws-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}
.ws-empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

/* Force delete confirmation modal light */
:deep(.modal-mask .modal-container) {
  background: #ffffff !important;
  border: 1px solid #e5e7eb !important;
}
:deep(.modal-mask .modal-header),
:deep(.modal-mask .modal-content),
:deep(.modal-mask form) {
  background: #ffffff !important;
}
:deep(.modal-mask .text-n-slate-12) {
  color: #111827 !important;
}
:deep(.modal-mask .text-n-slate-11) {
  color: #4b5563 !important;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
</style>
